import {
  Button,
  DataTable,
  Icon,
  PageSection,
  SegmentedControl,
  SegmentedControlItem,
  Spacer,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  type TranslationKeys,
} from '@pypestream/translations';
import { ColumnDef } from '@tanstack/react-table';
import { html } from 'lit/static-html.js';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  useDeleteReportingApiCredentialModal,
  useReportingApiCredentialModal,
} from '../../components/modals';
import { SettingsEmptyState } from '../../components/settings-empty-state';
import { SettingsSectionLoader } from '../../components/settings-section-loader';
import {
  renderSolutionChannelGroups,
  SOLUTION_GROUPS_TEST_ID_PREFIX,
  useChannelGroupingResolver,
  useSolutionGroupsTranslate,
} from '../../components/solution-channel-groups';
import { useLoadingState, useProStudioViewerRole } from '../../hooks';
import {
  getEnvDotStyle,
  groupChannelsBySolution,
  type ReportingApiCredentialRow,
  type SolutionChannelGroup,
} from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { PROJECT_SETTINGS_SECTIONS } from './sections';
import { useGoToChannel } from './use-go-to-channel';
import { useGoToSolutionVersions } from './use-go-to-solution-versions';

const section = PROJECT_SETTINGS_SECTIONS[1];

type Env = 'live' | 'sandbox';

const CHANNELS_MAX_WIDTH = 420;

// The full key is shown only once at creation; the list shows a masked key
// (dots + last 4) so it isn't exposed at rest.
const maskKey = (key: string): string =>
  key.length > 4 ? `${'•'.repeat(8)}${key.slice(-4)}` : key;

export const ReportingApiCredentials: FC = () => {
  const [env, setEnv] = useState<Env>('sandbox');
  // Keyed by `groupToggleKey` (credential id + solution id): one entry per
  // expanded solution box, not per table row.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  const apiCredentials = useProStudioCtxSelector((ctx) => ctx.apiCredentials);
  const solutions = useProStudioCtxSelector((ctx) => ctx.solutions);
  const isViewer = useProStudioViewerRole();

  const { openModal: openCredentialModal, reportingApiCredentialModal } =
    useReportingApiCredentialModal();
  const { openModal: openDeleteModal, deleteReportingApiCredentialModal } =
    useDeleteReportingApiCredentialModal();
  const goToChannel = useGoToChannel();
  const goToSolutionVersions = useGoToSolutionVersions();
  const translateSolutionGroups = useSolutionGroupsTranslate();

  // Load once. See live-agent-platforms.tsx for why this is gated on
  // useLoadingState() too, not just hasLoaded.
  const isPageLoading = useLoadingState();
  const hasLoaded = apiCredentials != null;
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadApiCredentials' });
    }
  }, [isPageLoading, hasLoaded]);

  const toggleExpanded = useCallback(
    (key: string) =>
      setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] })),
    []
  );

  // Memoized: the grouping below depends on its identity.
  const rows: ReportingApiCredentialRow[] = useMemo(
    () => apiCredentials?.[env] ?? [],
    [apiCredentials, env]
  );

  // Every row's solution boxes, built once per (rows, solutions) — not per cell
  // render. The resolver is `null` while the solutions are unknown, which makes
  // each row one flat, unresolved list instead of a false "No solution"
  // (`useChannelGroupingResolver`, shared with Live Agent Platforms).
  const resolveSolution = useChannelGroupingResolver(solutions);
  const groupsByCredentialId = useMemo(
    () =>
      new Map<string, SolutionChannelGroup[]>(
        rows.map((row) => [
          row.id,
          groupChannelsBySolution(row.channels, resolveSolution),
        ])
      ),
    [rows, resolveSolution]
  );

  const columns: ColumnDef<ReportingApiCredentialRow>[] = [
    {
      accessorKey: 'name',
      accessorFn: (row) => row.name,
      header: 'Name',
      cell: ({ row }) =>
        html`<div style="min-width: 160px;">
          <ps-text-subtitle size="small">${row.original.name}</ps-text-subtitle>
        </div>`,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) =>
        html`<ps-text-body size="small">
          ${row.original.description || '—'}
        </ps-text-body>`,
    },
    {
      accessorKey: 'channels',
      header: 'Channels',
      cell: ({ row }) => {
        const { id } = row.original;
        if (!row.original.channels.length) {
          return html`<ps-text-body size="small">—</ps-text-body>`;
        }
        return html`<div style=${`max-width: ${CHANNELS_MAX_WIDTH}px;`}>
          ${renderSolutionChannelGroups({
            groups: groupsByCredentialId.get(id) ?? [],
            ownerId: id,
            isExpanded: (key) => !!expandedGroups[key],
            onToggle: toggleExpanded,
            onGoToChannel: goToChannel,
            onGoToSolution: goToSolutionVersions,
            testIdPrefix: SOLUTION_GROUPS_TEST_ID_PREFIX.reportingApi,
            translate: translateSolutionGroups,
          })}
        </div>`;
      },
    },
    {
      accessorKey: 'key',
      header: 'Key',
      cell: ({ row }) =>
        html`<ps-text-body size="small" truncate
          >${maskKey(row.original.key)}</ps-text-body
        >`,
    },
    // Edit/Delete are the only entries, so viewers get no actions column.
    ...(isViewer
      ? []
      : [
          {
            id: 'actions',
            header: () =>
              html`<span style="opacity: 0; font-size: 4px;">Actions</span>`,
            cell: ({ row }) => html`
              <ps-stack justifyContent="end">
                <ps-icon-button
                  name="kebab"
                  size="small"
                  id=${`reporting-api-actions-${row.original.id}`}
                ></ps-icon-button>
              </ps-stack>
              <ps-dropdown
                trigger=${`reporting-api-actions-${row.original.id}`}
                placement="bottom-end"
              >
                <ps-menu>
                  <ps-menu-item
                    @click=${() =>
                      openCredentialModal('edit', {
                        env,
                        credentialId: row.original.id,
                        pypeId: row.original.pypeId,
                      })}
                  >
                    <ps-icon name="edit" size="small" slot="prefix"></ps-icon>
                    Edit
                  </ps-menu-item>
                  <ps-menu-item
                    variant="danger"
                    @click=${() =>
                      openDeleteModal({
                        credentialId: row.original.id,
                        pypeId: row.original.pypeId,
                        credentialName: row.original.name,
                        env,
                      })}
                  >
                    <ps-icon name="delete" size="small" slot="prefix"></ps-icon>
                    Delete
                  </ps-menu-item>
                </ps-menu>
              </ps-dropdown>
            `,
            meta: { align: 'end' },
          } as ColumnDef<ReportingApiCredentialRow>,
        ]),
  ];

  return (
    <PageSection size="small" test-id="ps-reporting-api-credentials-section">
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <TextTitle size="small">
          <TranslationComponent i18nKey={section.i18nKey as TranslationKeys}>
            {section.label}
          </TranslationComponent>
        </TextTitle>

        <Stack alignItems="center" gutter="medium" nowrap>
          <SegmentedControl
            test-id="ps-reporting-api-credentials-env"
            fitContent
            value={env}
            onChange={(e) => setEnv(e.detail.value as Env)}
          >
            <SegmentedControlItem value="sandbox">
              <span
                slot="prefix"
                style={getEnvDotStyle('sandbox', env === 'sandbox')}
              />
              Sandbox
            </SegmentedControlItem>
            <SegmentedControlItem value="live">
              <span
                slot="prefix"
                style={getEnvDotStyle('live', env === 'live')}
              />
              Live
            </SegmentedControlItem>
          </SegmentedControl>

          {!isViewer && (
            <Button
              test-id="ps-reporting-api-credentials-add"
              size="large"
              onClick={() => openCredentialModal('add', { env })}
            >
              <Icon name="add" slot="prefix" />
              Add Credential
            </Button>
          )}
        </Stack>
      </Stack>

      <Spacer size="xlarge" />

      {/*
        Wrapper is load-bearing: the modals below portal themselves out while open,
        so a branch swap at their sibling position throws in the DOM. See
        portaled-modal-sibling.test.tsx.
      */}
      <div>
        {!hasLoaded ? (
          <SettingsSectionLoader testId="ps-reporting-api-credentials-loading" />
        ) : rows.length === 0 ? (
          <SettingsEmptyState
            testId="ps-reporting-api-credentials-empty"
            title="No API credentials yet"
            description={`Add a credential to pull ${env === 'live' ? 'Live' : 'Sandbox'} conversation data through the Reporting API.`}
          />
        ) : (
          <DataTable
            test-id="ps-reporting-api-credentials-table"
            filterColumns={['name']}
            size="small"
            columns={columns}
            data={rows}
            actionable={false}
          />
        )}
      </div>

      {reportingApiCredentialModal}
      {deleteReportingApiCredentialModal}
    </PageSection>
  );
};
